import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import * as THREE from 'three'

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger)

interface ScrollControllerProps {
  particleSystemRef: React.RefObject<THREE.Points>
  geometriesRef: React.RefObject<THREE.Group>
}

export default function ScrollController({ particleSystemRef, geometriesRef }: ScrollControllerProps) {
  const { camera, scene } = useThree()
  const scrollProgress = useRef(0)
  
  useFrame(() => {
    // Apply scroll-based transformations every frame for smoothness
    const progress = scrollProgress.current
    
    if (camera) {
      // Much gentler camera movement to reduce lag
      camera.position.z = THREE.MathUtils.lerp(camera.position.z, 5 + (progress * 3), 0.03)
      camera.position.y = THREE.MathUtils.lerp(camera.position.y, progress * 0.5, 0.03)
      camera.rotation.x = THREE.MathUtils.lerp(camera.rotation.x, progress * 0.05, 0.03)
    }
    
    // Minimal particle rotation to reduce lag
    if (particleSystemRef.current) {
      const targetRotationY = progress * Math.PI * 0.1
      
      particleSystemRef.current.rotation.y = THREE.MathUtils.lerp(
        particleSystemRef.current.rotation.y, 
        targetRotationY, 
        0.02
      )
    }
    
    // Reduce geometry animations complexity
    if (geometriesRef.current && progress % 0.1 < 0.01) { // Only update occasionally
      geometriesRef.current.children.forEach((mesh, index) => {
        const phaseOffset = index * 0.1
        const targetScale = 1 + Math.sin(progress * Math.PI + phaseOffset) * 0.2
        
        mesh.scale.setScalar(THREE.MathUtils.lerp(mesh.scale.x, targetScale, 0.1))
      })
    }
  })
  
  useEffect(() => {
    // Main scroll trigger that updates the progress value
    ScrollTrigger.create({
      trigger: "body",
      start: "top top",
      end: "bottom bottom",
      scrub: 0, // Immediate response
      onUpdate: (self) => {
        scrollProgress.current = self.progress
      }
    })

    // Section-based camera movements with smooth transitions
    ScrollTrigger.create({
      trigger: "#about",
      start: "top bottom",
      end: "bottom top",
      scrub: 1,
      onUpdate: (self) => {
        const sectionProgress = self.progress
        if (camera) {
          gsap.to(camera.position, {
            x: -5 + (sectionProgress * 10),
            duration: 0.5,
            ease: "power2.out"
          })
        }
      }
    })

    ScrollTrigger.create({
      trigger: "#features", 
      start: "top bottom",
      end: "bottom top",
      scrub: 1,
      onUpdate: (self) => {
        const sectionProgress = self.progress
        if (camera) {
          gsap.to(camera.position, {
            x: 5 - (sectionProgress * 10),
            z: 8 + (sectionProgress * 5),
            duration: 0.5,
            ease: "power2.out"
          })
        }
      }
    })

    // Dynamic lighting based on scroll
    const lights = scene.children.filter(child => child.type.includes('Light'))
    lights.forEach((light, index) => {
      ScrollTrigger.create({
        trigger: "body",
        start: "top top",
        end: "bottom bottom",
        scrub: 1,
        onUpdate: (self) => {
          const progress = self.progress
          const baseIntensity = index === 0 ? 0.3 : index === 1 ? 1 : 0.5
          const newIntensity = baseIntensity + Math.sin(progress * Math.PI * 2) * 0.3
          
          if ('intensity' in light) {
            gsap.to(light, {
              intensity: newIntensity,
              duration: 0.3
            })
          }
        }
      })
    })

    // Particle material animation
    if (particleSystemRef.current?.material) {
      ScrollTrigger.create({
        trigger: "body",
        start: "top top",
        end: "bottom bottom",
        scrub: 1,
        onUpdate: (self) => {
          const progress = self.progress
          const material = particleSystemRef.current?.material as THREE.PointsMaterial
          
          if (material) {
            gsap.to(material, {
              size: 2 + Math.sin(progress * Math.PI * 4) * 2,
              opacity: 0.8 + Math.sin(progress * Math.PI * 2) * 0.2,
              duration: 0.2
            })
          }
        }
      })
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill())
    }
  }, [camera, scene, particleSystemRef, geometriesRef])

  return null
}
